/* eslint-disable */
import React, { FC, useEffect } from 'react';
import { Button, Input, Select, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import produce from 'immer';
import { mergeWith } from 'lodash';

import { Checkbox, DateInput, DocumentInput, LegalTypeSelect, LtrInput } from '~/components';
import Form from '~/components/WizardForm';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import buildNamePath from '~/utils/buildNamePath';

import { Draft } from '~/types/Draft';
import Section from '~/layouts/Section ';
import { useGetApplication, useGetPayloadWithProvided } from '~/controllers/wizard';

import CustomFields from '~/components/CustomFields';
import { Stage } from '~/types/Application';
import ActivitySelect from '~/pages/ksa/application/components/ActivitySelect';
import { KsaCompanyInformation } from '~/types/ksa/KsaCompanyInformation';
import { useCommonErrors } from '~/controllers/validation';
import { useHandbook } from '~/controllers/handbook';
import { useKsaCities } from '~/controllers/cities';
import MccCodeSelect from '../components/MccCodeSelect';
import ContentHeader from '~/components/ContentHeader';
import { GlobalOutlined } from '@ant-design/icons';
import WizardContinueButton from '~/layouts/WizardContinueButton';
import { useIsTablet } from '~/hooks/useScreenSizes';
import { useDirection } from '~/hooks/i18nHooks';
import paths from '~/pages/paths';
import { useHistory } from 'react-router';
import { getDecodedJwt } from '~/utils/getDecodedJwt';
import { getTrackingId } from '~/utils/handleGetUtm';
import { hasArabicChars, validateEnglishOnly, validateArabicOnly } from '~/utils/containsArabic';
import { validateWebsite } from '~/utils/urlValidation';
import useMerchantNationalAddress from '~/hooks/useMerchantNationalAddress';
import NationalAddressCertificate from './NationalAddressCertificate';



const CompanyInformation: FC = () => {
  const { t } = useTranslation('ksaApplication');
  const { data }: any = useGetApplication();
  const [form] = Form.useForm();
  const history = useHistory();
  const decodedToken: any = getDecodedJwt();
  const handbook = useHandbook()?.data?.handbooks;
  const dir = useDirection();          // 'ltr' | 'rtl'
  const isRTL = dir === 'rtl';

  const getAdditionalParams = (mccCode?: string) => {
    const additionalParamsGroup = handbook
      ?.find((item: any) => item?.key === 'mcc')
      ?.values?.flatMap((groupItem: any) => groupItem.values)
      ?.flatMap((valuesItem: any) => valuesItem.values)
      ?.find((item: any) => item?.apiValue === mccCode);

    if (!additionalParamsGroup) return {};

    return {
      ...additionalParamsGroup.additionalParams,
      businessNatureKey: additionalParamsGroup.localizedValue,
    };
  };

  const payload = useGetPayloadWithProvided();
  const nationalAddress = useMerchantNationalAddress(form, data?.uid, {
    ...payload, companyInformation: payload?.companyInformation as KsaCompanyInformation,
  });
  const parentCompanyLicenseNumber = (payload?.companyInformation as KsaCompanyInformation)
    ?.parentCompanyLicenseNumber;
  const [, handleError] = useCommonErrors();
  const isMobile = useIsTablet();

  // ── Product selection detection ──────────────────────────────────────────
  //
  // Two places in the payload store the selected products:
  //   1. payload.services  → set by ServicesForm (keys: nGeniusPos, eCommerce…)
  //   2. payload.products  → set by agent/pre-defined (keys: 'pos', 'e-commerce'…)
  //
  // IMPORTANT: use `payload` from useGetPayloadWithProvided() which merges
  // payload.provided into the root payload.  Reading from data?.payload
  // directly gives the raw/unmerged object and may miss provided values.
  const servicesPayload = (payload?.services || {}) as Record<string, { enabled?: boolean }>;

  const isPosSelected =
    !!(servicesPayload?.POS?.enabled)

  const isEcomSelected =
    !!(servicesPayload?.eCommerce?.enabled)

  // Spec: Store Address + City  → visible if POS or ECOM & POS
  const showPosFields = isPosSelected;
  // Spec: Company Website URL   → visible if ECOM or ECOM & POS
  const showWebsite = isEcomSelected;

  // ── Thiqah pre-fill values ───────────────────────────────────────────────
  const thiqahFullInfo = data?.payload?.thiqahFullInfo as any;
  const thiqahName: string = thiqahFullInfo?.name || '';
  const thiqahCityName: string = thiqahFullInfo?.headquarterCityName || '';
  // Website may be pre-supplied by Thiqah general address
  const thiqahWebsite: string = thiqahFullInfo?.address?.general?.website || '';

  const { data: citiesData } = useKsaCities();

  // City dropdown: populated from the API, sorted alphabetically
  const citiesList = [...(citiesData || [])].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  const cityOptions = citiesList.map((c) => ({
    label: c.displayName,
    value: c.thiqahKey,
  }));

  // Fallback to thiqahCityName if it's not present in the API response
  const currentCityValue = form.getFieldValue(nb(['city']));
  const activeCityValue = currentCityValue || thiqahCityName;

  if (
    activeCityValue &&
    !citiesList.some(
      (c) =>
        c.thiqahKey.toLowerCase() === activeCityValue.toLowerCase() ||
        c.displayName.toLowerCase() === activeCityValue.toLowerCase()
    )
  ) {
    cityOptions.unshift({
      label: activeCityValue,
      value: activeCityValue,
    });
  }

  // Normalize the selected/pre-filled city value to match the API's thiqahKey when loaded
  useEffect(() => {
    if (citiesData) {
      const currentCity = form.getFieldValue(nb(['city']));
      if (currentCity) {
        const matched = citiesData.find(
          (c) =>
            c.thiqahKey.toLowerCase() === currentCity.toLowerCase() ||
            c.displayName.toLowerCase() === currentCity.toLowerCase()
        );
        if (matched && matched.thiqahKey !== currentCity) {
          const currentCompanyInfo = form.getFieldValue('companyInformation') || {};
          const currentBusinessDetails = currentCompanyInfo.businessDetails || {};
          form.setFieldsValue({
            companyInformation: {
              ...currentCompanyInfo,
              businessDetails: {
                ...currentBusinessDetails,
                city: matched.thiqahKey,
              },
            },
          });
        }
      }
    }
  }, [citiesData, form]);

  // ── Trigger validation for wrong-language pre-fills on mount (AC4, AC6) ─
  useEffect(() => {
    if (!thiqahName) return;
    // After the form initialises, validate the two name fields so that
    // validation messages appear immediately if the Thiqah name is in the
    // wrong script for that field.
    const timer = setTimeout(() => {
      form
        .validateFields([
          nb(['merchantNameEnglish']),
          nb(['merchantNameArabic']),
        ])
        .catch(() => {
          // Validation errors are shown inline by antd — no action needed.
        });
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thiqahName]);


  function validateTaxRegistrationNumber(value: string) {
    if (!value) return Promise.resolve();
    if (String(value).length >= 10 && String(value).length <= 15) return Promise.resolve();
    return Promise.reject();
  }

  if (!data) return null;

  return (
    <>
      <ContentHeader
        title={t('Business Details')}
        subtitle={t('Enter your company details.')}
        stageNumber={3}
      />

      <Container isMobile={isMobile} dir={dir} className="CompanyInformation">
        <Form<any>
          stage={Stage.ksaApplication}
          payloadKey="companyInformation"
          name={location.pathname}
          handleDraftDataBeforeSave={(values, initialValues) => produce<{ companyInformation?: Partial<KsaCompanyInformation> }>(initialValues || {}, (draft) => {
            mergeWith(draft, values, (_previous: unknown, value: unknown) =>
              Array.isArray(value) ? value : undefined);
            if (!draft.companyInformation) draft.companyInformation = {};
            draft.companyInformation.nationalAddressReviewRequired = nationalAddress.failed ||
              draft.companyInformation.nationalAddressReviewRequired === true;
          })}
          parseBeforeSubmit={(values) => {
            const additionalParams = getAdditionalParams(values?.companyInformation?.mccCode);

            (window as any).dataLayer?.push({
              event: 'event_formsubmit',
              page_ln: isRTL ? 'ar' : 'en',
              page_name: window.location.href,
              page_source: document.referrer || 'direct',
              page_section: 'Business Details',
              user_id: localStorage.getItem('sob_temp_uid'),
              trackingId: getTrackingId(data),
            });

            const masterId = additionalParams?.masterId;
            const terminalId = additionalParams?.terminalId;

            // Normalise websiteUrl – strip leading https:// if user typed it so
            // we don't double-prefix; store the full value as-is.
            const rawWebsite = (values?.companyInformation as any)?.websiteUrl;
            const websiteUrl = rawWebsite || undefined;

            return {
              companyInformation: {
                ...values?.companyInformation,
                nationalAddressReviewRequired: nationalAddress.failed ||
                  values?.companyInformation?.nationalAddressReviewRequired === true,
                websiteUrl,
                masterId,
                terminalId,
                commercialName: (values?.companyInformation as any)?.legalName,
                businessNatureKey: additionalParams?.businessNatureKey,
              },
            };
          }}
          setInitialValues={(initialValues) => {
            // @ts-ignore
            const { companyType } = data.payload;
            const result = produce({ ...initialValues, companyType }, (draft: any) => {
              if (!draft.companyInformation) draft.companyInformation = {};
              if (!draft.companyInformation.businessDetails) draft.companyInformation.businessDetails = {};

              // Helper to get or migrate values
              const getMerchantNameEnglish = () => {
                return (
                  draft.companyInformation.businessDetails?.merchantNameEnglish ??
                  draft.businessDetails?.merchantNameEnglish ??
                  draft.companyInformation.merchantNameEn
                );
              };

              const getMerchantNameArabic = () => {
                return (
                  draft.companyInformation.businessDetails?.merchantNameArabic ??
                  draft.businessDetails?.merchantNameArabic ??
                  draft.companyInformation.merchantNameAr
                );
              };

              const getStoreAddressEnglish = () => {
                return (
                  draft.companyInformation.businessDetails?.storeAddressEnglish ??
                  draft.businessDetails?.storeAddressEnglish ??
                  draft.companyInformation.storeAddressEn
                );
              };

              const getStoreAddressArabic = () => {
                return (
                  draft.companyInformation.businessDetails?.storeAddressArabic ??
                  draft.businessDetails?.storeAddressArabic ??
                  draft.companyInformation.storeAddressAr
                );
              };

              const getCity = () => {
                return (
                  draft.companyInformation.businessDetails?.city ??
                  draft.businessDetails?.city ??
                  draft.companyInformation.city
                );
              };

              draft.companyInformation.businessDetails.merchantNameEnglish = getMerchantNameEnglish();
              draft.companyInformation.businessDetails.merchantNameArabic = getMerchantNameArabic();
              draft.companyInformation.businessDetails.storeAddressEnglish = getStoreAddressEnglish();
              draft.companyInformation.businessDetails.storeAddressArabic = getStoreAddressArabic();
              draft.companyInformation.businessDetails.city = getCity();

              // [AC2] Merchant Name (English) – always pre-fill with Thiqah name.
              // Validation on mount will catch wrong-language values (AC4).
              if (!draft.companyInformation.businessDetails.merchantNameEnglish && thiqahName) {
                draft.companyInformation.businessDetails.merchantNameEnglish = thiqahName;
              }

              // [AC3] Merchant Name (Arabic) – same Thiqah name in both fields.
              // Validation on mount will catch wrong-language values (AC6).
              if (!draft.companyInformation.businessDetails.merchantNameArabic && thiqahName) {
                draft.companyInformation.businessDetails.merchantNameArabic = thiqahName;
              }

              // [AC14] City pre-filled from Thiqah headquarterCityName
              if (!draft.companyInformation.businessDetails.city && thiqahCityName) {
                draft.companyInformation.businessDetails.city = thiqahCityName;
              }

              // Website – pre-fill from Thiqah if available
              if (!draft.companyInformation.websiteUrl && thiqahWebsite) {
                draft.companyInformation.websiteUrl = thiqahWebsite;
              }
            });
            return result;
          }}
          onFieldsChange={() => {
            if (
              form
                .getFieldsError()
                .filter((item) => item?.name?.includes('licenseExpiryDate') && item.errors.length > 0)
                .length > 0
            ) {
              handleError.onError([{ name: ['THIQAH'], errors: [] }]);
            }
          }}
          form={form}
          validateTrigger="onSubmit"
        >

          <Form.Item hidden name={n(['nationalAddressReviewRequired'])} />
          {nationalAddress?.pending && (
            <SpinnerWrapper>
              <Spin size="small" />
            </SpinnerWrapper>
          )}
          {nationalAddress?.failed && <NationalAddressCertificate />}

          {/* ── Hidden system fields ── */}
          <Form.Item hidden initialValue={data?.payload?.thiqahFullInfo?.activities} name={n(['mainActivity'])} />
          <Form.Item hidden initialValue={decodedToken?.website} name={n(['website'])} />
          <Form.Item hidden name={n(['commercialName'])}><Input /></Form.Item>
          <Form.Item hidden name={n(['legalTypeName'])}><LegalTypeSelect /></Form.Item>
          <Form.Item hidden name={n(['dateOfEstablishment'])}><DateInput disableDateRule="date <= today" /></Form.Item>
          <Form.Item hidden name={n(['licenseNumber'])}><LtrInput /></Form.Item>
          <Form.Item hidden name={n(['entityNumber'])}><LtrInput /></Form.Item>
          <Form.Item hidden name={n(['licenseIssueDate'])}><DateInput /></Form.Item>
          {parentCompanyLicenseNumber && (
            <Form.Item hidden name={n(['parentCompanyLicenseNumber'])}><Input /></Form.Item>
          )}
          <Form.Item hidden name={n(['mainActivity'])}><ActivitySelect /></Form.Item>
          <Form.Item name={n(['terminalId'])} hidden />
          <Form.Item name={n(['masterId'])} hidden />

          <Section>

            {/* ── 1. Merchant Name (English) ─────────────────────────────────── */}
            {/* [AC1, AC2, AC4, AC5, AC8] Mandatory; Arabic letters not allowed */}
            <Form.Item
              rules={[
                { required: true, message: t('Please enter your company name.') },
                { validator: validateEnglishOnly },
              ]}
              validateTrigger={['onBlur', 'onSubmit']}
              labelText={t('Merchant Name (English)')}
              name={nb(['merchantNameEnglish'])}
              extra={
                <HelperText>
                  {t('Enter your store or brand name (shown on receipts and SMS)')}
                </HelperText>
              }
            >
              {/* Always LTR regardless of app language */}
              <LtrFieldInput
                dir="ltr"
                placeholder={isRTL ? 'مثال: My Store' : 'e.g. My Store'}
              />
            </Form.Item>

            {/* ── 2. Merchant Name (Arabic) ──────────────────────────────────── */}
            {/* [AC1, AC3, AC6, AC7, AC8] Mandatory; English letters not allowed */}
            <Form.Item
              rules={[
                { required: true, message: t('Please enter your company name.') },
                { validator: validateArabicOnly },
              ]}
              validateTrigger={['onBlur', 'onSubmit']}
              labelText={t('Merchant Name (Arabic)')}
              name={nb(['merchantNameArabic'])}
              extra={
                <HelperText>
                  {t('Enter your store or brand name (shown on receipts and SMS)')}
                </HelperText>
              }
            >
              {/* Always RTL – Arabic input */}
              <RtlFieldInput
                dir="rtl"
                placeholder={isRTL ? 'مثال: متجري' : 'e.g. متجري'}
              />
            </Form.Item>

            {/* ── 3. Company Website URL – ECOM / ECOM & POS only ────────────── */}
            {/* [AC15] Spec: Visible if Product is ECOM or ECOM & POS */}
            {showWebsite && (
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t('Please enter your company website.'),
                  },
                  { validator: validateWebsite },
                ]}
                labelText={t('Company Website URL')}
                name={n(['websiteUrl'])}
                extra={
                  <HelperText>
                    {t('Please enter the company website in the following format:')}
                    <br />
                    {t('https://www.domain.com or https://domain.com')}
                  </HelperText>
                }
              >
                <WebsiteInput
                  $isRtl={dir === 'rtl'}
                  suffix={<GrayGlobalIcon />}
                  placeholder="https://www.domain.com"
                />
              </Form.Item>
            )}

            {/* ── 4. Nature of Business or Main Line of Business ─────────────── */}
            <Form.Item
              rules={[
                {
                  required: true,
                  message: t('Please enter your business activity.'),
                },
              ]}
              labelText={t('Nature of Business or Main Line of Business')}
              name={n(['mccCode'])}
            >
              <MccCodeSelect />
            </Form.Item>

            {/* ── 5. Tax Registration Number (Optional) ──────────────────────── */}
            <Form.Item
              optional
              labelText={t('Tax Registration Number')}
              name={n(['taxRegistrationCertificate', 'number'])}
              rules={[
                {
                  validator: (_: any, value: any) => validateTaxRegistrationNumber(value),
                  message: t('Please enter a valid tax registration number'),
                },
              ]}
            >
              <LtrFieldInput dir="ltr" inputMode="numeric" />
            </Form.Item>

            {/* ── 6. Store Address (English) – POS / ECOM & POS only ─────────── */}
            {/* [AC9, AC11, AC13] Visible if POS or ECOM & POS; Arabic not allowed */}
            {showPosFields && (
              <Form.Item
                rules={[
                  { required: true, message: t('Please enter store address in English') },
                  { validator: validateEnglishOnly, message: t('Please enter store address in English') },
                ]}
                validateTrigger={['onBlur', 'onSubmit']}
                labelText={t('Store Address (English)')}
                name={nb(['storeAddressEnglish'])}
                extra={
                  <HelperText>
                    {t(
                      'Please enter the full English address (district and street name). This address will be used for POS device delivery.'
                    )}
                  </HelperText>
                }
              >
                <LtrFieldInput dir="ltr" />
              </Form.Item>
            )}

            {/* ── 7. Store Address (Arabic) – POS / ECOM & POS only ──────────── */}
            {/* [AC9, AC12, AC13] Visible if POS or ECOM & POS; English not allowed */}
            {showPosFields && (
              <Form.Item
                rules={[
                  { required: true, message: t('Please enter store address in Arabic') },
                  { validator: validateArabicOnly, message: t('Please enter store address in Arabic') },
                ]}
                validateTrigger={['onBlur', 'onSubmit']}
                labelText={t('Store Address (Arabic)')}
                name={nb(['storeAddressArabic'])}
                extra={
                  <HelperText>
                    {t(
                      'Please enter the full Arabic address (district and street name). This address will be used for POS device delivery.'
                    )}
                  </HelperText>
                }
              >
                <RtlFieldInput dir="rtl" />
              </Form.Item>
            )}

            {/* ── 8. Select City (Dropdown) – POS / ECOM & POS only ──────────── */}
            {/* [AC9, AC14] Dropdown; pre-filled from Thiqah headquarterCityName */}
            {showPosFields && (
              <Form.Item
                rules={[{ required: true, message: t('Please enter your city') }]}
                labelText={t('Select City')}
                name={nb(['city'])}
              >
                <Select
                  placeholder={t('Select')}
                  options={cityOptions}
                  allowClear={false}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={null}
                />
              </Form.Item>
            )}

          </Section>

          {/* ── Tax certificate section – hidden in this design ── */}
          <Section formItemLast className="hiddenSection">
            <Typography.Title level={4}>
              {t('Tax registration certificate').toUpperCase()}
            </Typography.Title>
            <Form.Item name={taxCertEnabledNamePath} valuePropName="checked" initialValue={false}>
              <Checkbox>{t('Yes, I have a Tax registration certificate.')}</Checkbox>
            </Form.Item>
            <Form.Visible name={taxCertEnabledNamePath}>
              <Typography.Paragraph>
                {t('Please upload the Tax registration certificate photo.')}
              </Typography.Paragraph>
              <Form.Item name={n(['taxRegistrationCertificate', 'files'])}>
                <DocumentInput name="photos" variant="default" />
              </Form.Item>
              <Typography.Title level={5}>{t('Document data:')}</Typography.Title>
              <InputsGroupLayout>
                <Form.Item
                  label={t('Tax registration number')}
                  name={n(['taxRegistrationCertificate', 'number'])}
                >
                  <Input />
                </Form.Item>
              </InputsGroupLayout>
            </Form.Visible>
          </Section>

          <CustomFields />

          <StyledContinueButton
            id="wizardContinueButtonConfirm"
            formName={location.pathname}
          />

          {isMobile && (
            <ContactUsButton
              type="text"
              onClick={() => history.push(paths.ksa.contactUs)}
            >
              {t('Contact Us')}
            </ContactUsButton>
          )}
        </Form>
      </Container>
    </>
  );
};

// ── Named paths ──────────────────────────────────────────────────────────────
const n = buildNamePath('companyInformation');
const nb = buildNamePath(['companyInformation', 'businessDetails']);
const taxCertEnabledNamePath = n(['taxRegistrationCertificate', 'enabled']);

// ── Styled components ────────────────────────────────────────────────────────

const Container = styled.div<{ isMobile?: boolean; dir?: string }>`
  /* Desktop: 60% width; tablet/mobile: 100% */
  width: 100%;
  max-width: 570px;
  min-width: 0;
  box-sizing: border-box;

  /* Global direction for the whole form */
  direction: ${({ dir }) => dir || 'ltr'};

  /* antd overrides for RTL layout */
  .ant-form-item-label {
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  }
  .ant-form-item-explain {
    text-align: ${({ dir }) => (dir === 'rtl' ? 'right' : 'left')};
  }
  .ant-select-selector {
    direction: ${({ dir }) => dir || 'ltr'};
  }

  .ant-form-item {
    max-width: 570px;
    width: 100%;
  }

  /* Hidden sections (e.g. tax certificate – not shown in current design) */
  .hiddenSection {
    display: none;
  }

  /* Desktop: constrain to 60% */
  @media (min-width: 769px) {
    width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
    max-width: 570px;
  }

  /* Mobile tweaks */
  @media (max-width: 768px) {
    width: 100%;
  }
`;

/** English-only input: always LTR, left-aligned text */
const LtrFieldInput = styled(Input)`
  direction: ltr !important;
  text-align: left !important;
  height: 56px !important;
  border-radius: 8px !important;
  padding: 16px 12px !important;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }

  input {
    direction: ltr !important;
    text-align: left !important;
    height: 100% !important;
    padding: 0 !important;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
`;

/** Arabic-only input: always RTL, right-aligned text */
const RtlFieldInput = styled(Input)`
  direction: rtl !important;
  text-align: right !important;
  height: 56px !important;
  border-radius: 8px !important;
  padding: 16px 12px !important;
  input {
    direction: rtl !important;
    text-align: right !important;
    height: 100% !important;
    padding: 0 !important;
  }
`;

/** Website URL input: always LTR */
const WebsiteInput = styled(Input) <{ $isRtl?: boolean }>`
  height: 56px !important;
  border-radius: 8px !important;
  padding: 0 12px !important;
  input {
    direction: ltr !important;
    text-align: ${({ $isRtl }) => ($isRtl ? 'right' : 'left')} !important;
    height: 100% !important;
    padding: 0 !important;
  }
`;

/** Grey icon used as suffix inside the website URL input */
const GrayGlobalIcon = styled(GlobalOutlined)`
  color: #8c8c8c;
`;

const HelperText = styled(Typography.Text)`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #595959;
  line-height: 1.5;
  max-width: 570px;
`;

const ContactUsButton = styled(Button)`
  width: 100%;
  max-width: 570px;
  margin-top: 20px;
  font-weight: 600;
  color: #0069b1;
`;

/** Continue button: scoped height & radius for Business Details module only */
const StyledContinueButton = styled(WizardContinueButton)`
  height: 60px !important;
  min-height: 60px !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  width: 100% !important;
  max-width: 570px !important;
  box-sizing: border-box !important;
  @media (max-width: 768px) {
    height: 56px !important;
  }
`;

/** Wrapper for the national address lookup spinner — gives it breathing room below */
const SpinnerWrapper = styled.div`
  margin-bottom: 16px;
`;

export default CompanyInformation;
